import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  Button,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  makeStyles,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Switch,
  FormControlLabel,
} from '@material-ui/core';
import GitHubIcon from '@material-ui/icons/GitHub';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import InfoIcon from '@material-ui/icons/Info';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import SettingsIcon from '@material-ui/icons/Settings';
import TimelineIcon from '@material-ui/icons/Timeline';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import NotificationsIcon from '@material-ui/icons/Notifications';
import { useApi } from '@backstage/core-plugin-api';
import { kubernetesApiRef } from '@backstage/plugin-kubernetes';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  integrationCard: {
    height: '100%',
    position: 'relative',
  },
  statusChip: {
    fontWeight: 'bold',
  },
  workflowDiagram: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  stepCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  activeStep: {
    backgroundColor: theme.palette.action.selected,
  },
  completedStep: {
    backgroundColor: theme.palette.success.light,
  },
  errorStep: {
    backgroundColor: theme.palette.error.light,
  },
  configSection: {
    marginBottom: theme.spacing(3),
  },
  formControl: {
    minWidth: 200,
    marginRight: theme.spacing(2),
  },
  triggerButton: {
    marginTop: theme.spacing(2),
  },
  logContainer: {
    backgroundColor: '#1e1e1e',
    color: '#d4d4d4',
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    maxHeight: 300,
    overflow: 'auto',
  },
}));

// 工作流配置接口
interface WorkflowConfig {
  name: string;
  description: string;
  pipeline: string;
  argoApp: string;
  trigger: 'manual' | 'push' | 'tag' | 'pullRequest';
  autoSync: boolean;
  syncOptions: {
    prune: boolean;
    selfHeal: boolean;
    validate: boolean;
  };
  rollbackStrategy: 'manual' | 'auto';
  notifications: {
    slack?: string;
    email?: string;
  };
}

// 工作流执行状态
interface WorkflowExecution {
  id: string;
  workflow: string;
  status: 'pending' | 'running' | 'success' | 'failed';
  steps: WorkflowStep[];
  startTime: string;
  endTime?: string;
  triggeredBy: string;
  gitCommit?: string;
}

// 工作流步骤
interface WorkflowStep {
  name: string;
  type: 'pipeline' | 'argoSync' | 'validation' | 'rollback';
  status: 'pending' | 'running' | 'success' | 'failed' | 'skipped';
  message?: string;
  startTime?: string;
  endTime?: string;
}

// 预定义的工作流模板
const workflowTemplates: WorkflowConfig[] = [
  {
    name: 'standard-ci-cd',
    description: '标准 CI/CD 流程：构建 -> 测试 -> 部署',
    pipeline: 'build-deploy-pipeline',
    argoApp: '',
    trigger: 'push',
    autoSync: true,
    syncOptions: {
      prune: true,
      selfHeal: true,
      validate: true,
    },
    rollbackStrategy: 'manual',
    notifications: {},
  },
  {
    name: 'canary-deployment',
    description: '金丝雀部署：渐进式发布',
    pipeline: 'canary-pipeline',
    argoApp: '',
    trigger: 'manual',
    autoSync: false,
    syncOptions: {
      prune: false,
      selfHeal: false,
      validate: true,
    },
    rollbackStrategy: 'auto',
    notifications: {},
  },
  {
    name: 'blue-green',
    description: '蓝绿部署：零停机切换',
    pipeline: 'blue-green-pipeline',
    argoApp: '',
    trigger: 'tag',
    autoSync: false,
    syncOptions: {
      prune: true,
      selfHeal: false,
      validate: true,
    },
    rollbackStrategy: 'manual',
    notifications: {},
  },
];

// 工作流配置对话框
const WorkflowConfigDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSave: (config: WorkflowConfig) => void;
  initialConfig?: WorkflowConfig;
}> = ({ open, onClose, onSave, initialConfig }) => {
  const [config, setConfig] = useState<WorkflowConfig>(
    initialConfig || workflowTemplates[0]
  );
  const [activeStep, setActiveStep] = useState(0);

  const handleNext = () => {
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSave = () => {
    onSave(config);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>配置 ArgoCD + Tekton 工作流</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} orientation="vertical">
          <Step>
            <StepLabel>基础配置</StepLabel>
            <StepContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="工作流名称"
                    value={config.name}
                    onChange={e => setConfig({ ...config, name: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="描述"
                    value={config.description}
                    onChange={e => setConfig({ ...config, description: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>选择模板</InputLabel>
                    <Select
                      value={config.name}
                      onChange={e => {
                        const template = workflowTemplates.find(t => t.name === e.target.value);
                        if (template) setConfig(template);
                      }}
                    >
                      {workflowTemplates.map(template => (
                        <MenuItem key={template.name} value={template.name}>
                          {template.description}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Box mt={2}>
                <Button onClick={handleNext} variant="contained" color="primary">
                  下一步
                </Button>
              </Box>
            </StepContent>
          </Step>

          <Step>
            <StepLabel>Pipeline 配置</StepLabel>
            <StepContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Tekton Pipeline"
                    value={config.pipeline}
                    onChange={e => setConfig({ ...config, pipeline: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="ArgoCD Application"
                    value={config.argoApp}
                    onChange={e => setConfig({ ...config, argoApp: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>触发方式</InputLabel>
                    <Select
                      value={config.trigger}
                      onChange={e => setConfig({ ...config, trigger: e.target.value as any })}
                    >
                      <MenuItem value="manual">手动触发</MenuItem>
                      <MenuItem value="push">Git Push</MenuItem>
                      <MenuItem value="tag">Git Tag</MenuItem>
                      <MenuItem value="pullRequest">Pull Request</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Box mt={2}>
                <Button onClick={handleBack}>上一步</Button>
                <Button onClick={handleNext} variant="contained" color="primary">
                  下一步
                </Button>
              </Box>
            </StepContent>
          </Step>

          <Step>
            <StepLabel>同步选项</StepLabel>
            <StepContent>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.autoSync}
                    onChange={e => setConfig({ ...config, autoSync: e.target.checked })}
                  />
                }
                label="自动同步"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={config.syncOptions.prune}
                    onChange={e => setConfig({
                      ...config,
                      syncOptions: { ...config.syncOptions, prune: e.target.checked }
                    })}
                  />
                }
                label="删除孤立资源 (Prune)"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={config.syncOptions.selfHeal}
                    onChange={e => setConfig({
                      ...config,
                      syncOptions: { ...config.syncOptions, selfHeal: e.target.checked }
                    })}
                  />
                }
                label="自动修复 (Self Heal)"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={config.syncOptions.validate}
                    onChange={e => setConfig({
                      ...config,
                      syncOptions: { ...config.syncOptions, validate: e.target.checked }
                    })}
                  />
                }
                label="验证资源"
              />
              <Box mt={2}>
                <FormControl fullWidth>
                  <InputLabel>回滚策略</InputLabel>
                  <Select
                    value={config.rollbackStrategy}
                    onChange={e => setConfig({ ...config, rollbackStrategy: e.target.value as any })}
                  >
                    <MenuItem value="manual">手动回滚</MenuItem>
                    <MenuItem value="auto">自动回滚（失败时）</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box mt={2}>
                <Button onClick={handleBack}>上一步</Button>
                <Button onClick={handleSave} variant="contained" color="primary">
                  保存配置
                </Button>
              </Box>
            </StepContent>
          </Step>
        </Stepper>
      </DialogContent>
    </Dialog>
  );
};

// 工作流执行视图
const WorkflowExecutionView: React.FC<{ execution: WorkflowExecution }> = ({ execution }) => {
  const classes = useStyles();

  const getStepIcon = (step: WorkflowStep) => {
    switch (step.status) {
      case 'success':
        return <CheckCircleIcon style={{ color: '#4caf50' }} />;
      case 'running':
        return <CircularProgress size={20} />;
      case 'failed':
        return <ErrorIcon style={{ color: '#f44336' }} />;
      default:
        return <InfoIcon style={{ color: '#9e9e9e' }} />;
    }
  };

  const getStepClassName = (step: WorkflowStep) => {
    switch (step.status) {
      case 'success':
        return classes.completedStep;
      case 'running':
        return classes.activeStep;
      case 'failed':
        return classes.errorStep;
      default:
        return '';
    }
  };

  return (
    <Card className={classes.integrationCard}>
      <CardHeader
        title={`工作流: ${execution.workflow}`}
        subheader={`触发者: ${execution.triggeredBy} | 开始时间: ${new Date(execution.startTime).toLocaleString()}`}
        action={
          <Chip
            label={execution.status}
            color={execution.status === 'success' ? 'primary' : 'default'}
            size="small"
          />
        }
      />
      <CardContent>
        <Box className={classes.workflowDiagram}>
          <Typography variant="h6" gutterBottom>
            执行步骤
          </Typography>
          {execution.steps.map((step, index) => (
            <Box
              key={index}
              className={`${classes.stepCard} ${getStepClassName(step)}`}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                  {getStepIcon(step)}
                  <Typography variant="subtitle1">{step.name}</Typography>
                  <Chip label={step.type} size="small" variant="outlined" />
                </Box>
                <Typography variant="caption" color="textSecondary">
                  {step.startTime && new Date(step.startTime).toLocaleTimeString()}
                </Typography>
              </Box>
              {step.message && (
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                  {step.message}
                </Typography>
              )}
            </Box>
          ))}
        </Box>

        {execution.gitCommit && (
          <Box mt={2}>
            <Typography variant="subtitle2" gutterBottom>
              Git Commit
            </Typography>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <GitHubIcon fontSize="small" />
              <Typography variant="body2" style={{ fontFamily: 'monospace' }}>
                {execution.gitCommit}
              </Typography>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// 主集成组件
export const ArgoTektonIntegration: React.FC = () => {
  const classes = useStyles();
  const kubernetesApi = useApi(kubernetesApiRef);
  const [workflows, setWorkflows] = useState<WorkflowConfig[]>([]);
  const [executions, setExecutions] = useState<WorkflowExecution[]>([]);
  const [loading, setLoading] = useState(true);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);

  // 模拟数据
  useEffect(() => {
    // 模拟加载工作流
    setTimeout(() => {
      setWorkflows([
        {
          name: 'main-deployment',
          description: '主分支自动部署',
          pipeline: 'build-deploy-pipeline',
          argoApp: 'example-app',
          trigger: 'push',
          autoSync: true,
          syncOptions: { prune: true, selfHeal: true, validate: true },
          rollbackStrategy: 'manual',
          notifications: { slack: '#deployments' },
        },
        {
          name: 'release-workflow',
          description: '版本发布流程',
          pipeline: 'release-pipeline',
          argoApp: 'example-app',
          trigger: 'tag',
          autoSync: false,
          syncOptions: { prune: true, selfHeal: false, validate: true },
          rollbackStrategy: 'auto',
          notifications: { slack: '#releases', email: 'team@example.com' },
        },
      ]);

      setExecutions([
        {
          id: '1',
          workflow: 'main-deployment',
          status: 'success',
          steps: [
            { name: '构建镜像', type: 'pipeline', status: 'success', message: '镜像构建成功: v1.2.3' },
            { name: '运行测试', type: 'pipeline', status: 'success', message: '所有测试通过' },
            { name: '同步到 ArgoCD', type: 'argoSync', status: 'success', message: '应用已同步' },
            { name: '验证部署', type: 'validation', status: 'success', message: '健康检查通过' },
          ],
          startTime: new Date(Date.now() - 3600000).toISOString(),
          endTime: new Date(Date.now() - 3000000).toISOString(),
          triggeredBy: 'git-push',
          gitCommit: 'abc123def',
        },
        {
          id: '2',
          workflow: 'release-workflow',
          status: 'running',
          steps: [
            { name: '构建发布镜像', type: 'pipeline', status: 'success' },
            { name: '创建发布标签', type: 'pipeline', status: 'success' },
            { name: '部署到生产', type: 'argoSync', status: 'running', message: '正在同步...' },
            { name: '生产验证', type: 'validation', status: 'pending' },
          ],
          startTime: new Date(Date.now() - 600000).toISOString(),
          triggeredBy: 'manual',
          gitCommit: 'def456ghi',
        },
      ]);

      setLoading(false);
    }, 1000);
  }, []);

  // 触发工作流
  const triggerWorkflow = async (workflow: WorkflowConfig) => {
    try {
      // 创建 PipelineRun
      const pipelineRunName = `${workflow.pipeline}-${Date.now()}`;
      
      const pipelineRun = {
        apiVersion: 'tekton.dev/v1beta1',
        kind: 'PipelineRun',
        metadata: {
          name: pipelineRunName,
          namespace: 'tekton-pipelines',
          labels: {
            'argocd.integration': 'true',
            'workflow.name': workflow.name,
            'argocd.app': workflow.argoApp,
          },
          annotations: {
            'argocd.sync': workflow.autoSync ? 'true' : 'false',
            'workflow.trigger': workflow.trigger,
          },
        },
        spec: {
          pipelineRef: {
            name: workflow.pipeline,
          },
          params: [
            { name: 'argoApp', value: workflow.argoApp },
            { name: 'autoSync', value: workflow.autoSync.toString() },
          ],
        },
      };

      await kubernetesApi.proxy({
        clusterName: 'local-cluster',
        path: '/apis/tekton.dev/v1beta1/namespaces/tekton-pipelines/pipelineruns',
        init: {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(pipelineRun),
        },
      });

      // 添加新的执行记录
      const newExecution: WorkflowExecution = {
        id: Date.now().toString(),
        workflow: workflow.name,
        status: 'running',
        steps: [
          { name: '初始化', type: 'pipeline', status: 'running' },
          { name: '构建', type: 'pipeline', status: 'pending' },
          { name: 'ArgoCD 同步', type: 'argoSync', status: 'pending' },
          { name: '验证', type: 'validation', status: 'pending' },
        ],
        startTime: new Date().toISOString(),
        triggeredBy: 'manual',
      };

      setExecutions(prev => [newExecution, ...prev]);
    } catch (error) {
      // Failed to trigger workflow
    }
  };

  const handleSaveWorkflow = (config: WorkflowConfig) => {
    setWorkflows(prev => [...prev, config]);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {/* 标题栏 */}
      <Box className={classes.header} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h5">ArgoCD + Tekton 集成</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SettingsIcon />}
          onClick={() => setConfigDialogOpen(true)}
        >
          配置工作流
        </Button>
      </Box>

      {/* 工作流列表 */}
      <Typography variant="h6" gutterBottom>
        配置的工作流
      </Typography>
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        {workflows.map(workflow => (
          <Grid item xs={12} md={6} key={workflow.name}>
            <Card>
              <CardHeader
                title={workflow.name}
                subheader={workflow.description}
                action={
                  <Box>
                    <Chip 
                      label={workflow.trigger} 
                      size="small" 
                      variant="outlined"
                      style={{ marginRight: 8 }}
                    />
                    {workflow.autoSync && (
                      <Chip 
                        label="自动同步" 
                        size="small" 
                        color="primary"
                        icon={<AutorenewIcon />}
                      />
                    )}
                  </Box>
                }
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      Pipeline
                    </Typography>
                    <Typography variant="body1">{workflow.pipeline}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      ArgoCD App
                    </Typography>
                    <Typography variant="body1">{workflow.argoApp}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      同步选项
                    </Typography>
                    <Box display="flex" style={{ gap: 8 }}>
                      {workflow.syncOptions.prune && (
                        <Chip label="Prune" size="small" />
                      )}
                      {workflow.syncOptions.selfHeal && (
                        <Chip label="Self Heal" size="small" />
                      )}
                      {workflow.syncOptions.validate && (
                        <Chip label="Validate" size="small" />
                      )}
                    </Box>
                  </Grid>
                  {(workflow.notifications.slack || workflow.notifications.email) && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        通知
                      </Typography>
                      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <NotificationsIcon fontSize="small" />
                        {workflow.notifications.slack && (
                          <Typography variant="caption">{workflow.notifications.slack}</Typography>
                        )}
                        {workflow.notifications.email && (
                          <Typography variant="caption">{workflow.notifications.email}</Typography>
                        )}
                      </Box>
                    </Grid>
                  )}
                </Grid>
                <Box mt={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<PlayArrowIcon />}
                    onClick={() => triggerWorkflow(workflow)}
                  >
                    触发工作流
                  </Button>
                  <Button
                    size="small"
                    startIcon={<TimelineIcon />}
                    style={{ marginLeft: 8 }}
                  >
                    查看历史
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* 执行历史 */}
      <Typography variant="h6" gutterBottom>
        最近执行
      </Typography>
      <Box>
        {executions.map(execution => (
          <Box key={execution.id} mb={2}>
            <WorkflowExecutionView execution={execution} />
          </Box>
        ))}
      </Box>

      {/* 配置对话框 */}
      <WorkflowConfigDialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        onSave={handleSaveWorkflow}
      />
    </Box>
  );
};

export default ArgoTektonIntegration;
